import type { Module } from "@/lib/mapper/modules/types";
import type { Profile, Templatable } from "@/lib/mapper/types";
import { imageSlotsOf, slotColumn } from "@/lib/mapper/images";
import { toTemplateValue } from "@/modules/_shared/templateValue";

const IMAGE_OUTPUT_COLUMNS = ["Image Src", "Image Position", "Image Alt Text"] as const;

/** Template as readable text: fields as "/Name". */
function templateLabel(value: Templatable): string {
  return toTemplateValue(value)
    .map((seg) => (seg.t === "text" ? seg.v : `/${seg.name}`))
    .join("");
}

/** Short label for a slot: its column name, or the template text. */
function slotLabel(profile: Profile, index: number): string {
  const slot = imageSlotsOf(profile.images)[index];
  if (!slot) return "";
  return slotColumn(slot) ?? (templateLabel(slot.src).trim() || `Image ${index + 1}`);
}

export const imageFanoutModule: Module = {
  id: "image-fanout-shopify",
  name: "Images",
  description:
    "Turns image columns into Shopify image rows: the first image on the product row, " +
    "each extra image on its own row with the same Handle, a Position and Alt text.",
  glyph: "🖼",
  tone: "teal",
  authoritative: true,
  ownedColumns(profile: Profile): string[] {
    if (!this.isActive(profile)) return [];
    return [...IMAGE_OUTPUT_COLUMNS];
  },
  isActive(profile: Profile): boolean {
    return imageSlotsOf(profile.images).length > 0;
  },
  summarize(profile: Profile) {
    const count = imageSlotsOf(profile.images).length;
    const labels = Array.from({ length: count }, (_, i) => slotLabel(profile, i));
    const transform = profile.images?.transform ?? "none";
    const columnList =
      labels.length > 4
        ? `${labels.slice(0, 4).join(", ")}, +${labels.length - 4} more`
        : labels.join(", ");
    const countLabel = `${count} source column${count === 1 ? "" : "s"}`;
    const transformLabel =
      transform === "dropbox-direct"
        ? "Dropbox share links → direct download URLs"
        : "URLs passed through unchanged";
    const customAlt = imageSlotsOf(profile.images).some((s) => s.alt !== undefined) ||
      profile.images?.alt !== undefined;
    return {
      text:
        count === 0
          ? "No image columns selected for Image Src"
          : `${countLabel}: ${columnList} to Image Src`,
      bullets: [
        `Sources (in position order): ${labels.join(", ") || "none configured"}`,
        transformLabel,
        customAlt ? "Alt text: custom per image" : "Alt text: product Title",
        "Extra images become extra Shopify rows with the same Handle.",
      ],
    };
  },
};
